import React, { useEffect, useState } from 'react';
import { getStatistiques, getPartenaires, getForages, getLocalites, getSecteurs } from '../services/api';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS_PIE = ['#4CAF50', '#f44336'];
const CAT_COLORS = ['#2196F3', '#333', '#4CAF50', '#FF9800', '#9C27B0', '#009688', '#795548', '#607D8F'];
const BAR_COLORS = ['#1976d2', '#FF9800', '#4CAF50', '#e53935', '#9C27B0', '#00897B', '#5D4037', '#546E7A', '#D81B60'];

const Statistiques: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [forages, setForages] = useState<any[]>([]);
  const [localites, setLocalites] = useState<any[]>([]);
  const [secteurs, setSecteurs] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({ annee: new Date().getFullYear().toString() });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { getPartenaires().then(r => setPartenaires(r.data)); }, []);

  useEffect(() => {
    if (filters.partenaire_id) {
      getForages({ partenaire_id: filters.partenaire_id }).then(r => setForages(r.data));
      getLocalites({ partenaire_id: filters.partenaire_id }).then(r => setLocalites(r.data));
      getSecteurs({ partenaire_id: filters.partenaire_id }).then(r => setSecteurs(r.data));
    } else {
      setForages([]); setLocalites([]); setSecteurs([]);
    }
  }, [filters.partenaire_id]);

  const fetchStats = () => {
    setRefreshing(true);
    const params: Record<string, string> = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v as string; });
    getStatistiques(params).then(r => setStats(r.data)).finally(() => setRefreshing(false));
  };

  useEffect(() => { fetchStats(); }, [filters]);

  const moisOptions = [
    { value: '', label: 'Tous les mois' },
    { value: '1', label: 'Janvier' }, { value: '2', label: 'Fevrier' },
    { value: '3', label: 'Mars' }, { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' }, { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' }, { value: '8', label: 'Aout' },
    { value: '9', label: 'Septembre' }, { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' }, { value: '12', label: 'Decembre' }
  ];

  const hasPartenaire = !!filters.partenaire_id;
  const fmt = (n: number) => Number(n || 0).toLocaleString('fr-FR');

  const pieData = stats ? [
    { name: 'Payees', value: Number(stats.totalPaye || 0) },
    { name: 'Impayees', value: Math.max(0, Number(stats.montantARecouvrer || 0)) }
  ].filter(d => d.value > 0) : [];

  const kpiColor = (val: number, good: number, warn: number) =>
    val >= good ? '#2e7d32' : val >= warn ? '#e65100' : '#c62828';

  return (
    <div>
      {/* Header bar */}
      <div style={{
        background: '#1976d2', color: '#fff', padding: '12px 20px', borderRadius: 8,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10
      }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Statistiques</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filters.partenaire_id || ''} onChange={e => {
            setFilters({ ...filters, partenaire_id: e.target.value, localite_id: '', secteur_id: '', forage_id: '' });
          }} style={selStyle}>
            <option value="">Tous les partenaires</option>
            {partenaires.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
          </select>
          {hasPartenaire && (
            <>
              <select value={filters.forage_id || ''} onChange={e => setFilters({ ...filters, forage_id: e.target.value })} style={selStyle}>
                <option value="">Tous les forages</option>
                {forages.map(f => <option key={f.id} value={f.id}>{f.libelle || f.nom}</option>)}
              </select>
              <select value={filters.localite_id || ''} onChange={e => setFilters({ ...filters, localite_id: e.target.value })} style={selStyle}>
                <option value="">Toutes les localites</option>
                {localites.map(l => <option key={l.id} value={l.id}>{l.libelle}</option>)}
              </select>
              <select value={filters.secteur_id || ''} onChange={e => setFilters({ ...filters, secteur_id: e.target.value })} style={selStyle}>
                <option value="">Tous les secteurs</option>
                {secteurs.map(s => <option key={s.id} value={s.id}>{s.libelle}</option>)}
              </select>
            </>
          )}
          <select value={filters.mois || ''} onChange={e => setFilters({ ...filters, mois: e.target.value })} style={selStyle}>
            {moisOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select value={filters.annee || ''} onChange={e => setFilters({ ...filters, annee: e.target.value })} style={selStyle}>
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={fetchStats} disabled={refreshing} style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: refreshing ? '#ccc' : '#fff', color: '#1976d2',
            cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>&#8635;</span>
            {refreshing ? 'Chargement...' : 'Actualiser'}
          </button>
        </div>
      </div>

      {stats && (
        <>
          {/* === TOP STAT BOXES === */}
          <div className="stat-boxes">
            <div className="stat-box bg-blue">
              <h4>Abonnes</h4>
              <div className="stat-box-value">{fmt(stats.abonnes)}</div>
            </div>
            <div className="stat-box bg-orange">
              <h4>Nombre de Factures</h4>
              <div className="stat-box-value">{fmt(stats.nbFactures)}</div>
            </div>
            <div className="stat-box bg-green">
              <h4>Volume Facture</h4>
              <div className="stat-box-value">{fmt(stats.volumeTotal)} m<sup>3</sup></div>
            </div>
            <div className="stat-box bg-red">
              <h4>Production</h4>
              <div className="stat-box-value">{fmt(stats.production)} m<sup>3</sup></div>
            </div>
            <div className="stat-box bg-teal">
              <h4>Cons. Elect / Gasoil</h4>
              <div className="stat-box-value">{fmt(stats.consElec)} kW / {fmt(stats.gasoil)} L</div>
            </div>
            <div className="stat-box bg-purple">
              <h4>Tmps Pomp / Grp</h4>
              <div className="stat-box-value">{fmt(stats.tempsPompage)}H / {fmt(stats.tempsGroupe)}H</div>
            </div>
          </div>

          {/* === KPI INDICATORS === */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16, marginBottom: 20 }}>
            <KpiCard label="Taux de Recouvrement" value={`${stats.tauxRecouvrement}%`} color={kpiColor(stats.tauxRecouvrement, 70, 40)} icon="&#128200;" />
            <KpiCard label="Rendement Reseau" value={stats.rendement > 0 ? `${stats.rendement}%` : 'N/A'} color={stats.rendement > 0 ? kpiColor(stats.rendement, 80, 60) : '#78909C'} icon="&#128167;" />
            <KpiCard label="Conso Moyenne / Facture" value={`${fmt(stats.consoMoyenne)} m³`} color="#1565c0" icon="&#128202;" />
            <KpiCard label="Montant Moyen / Facture" value={`${fmt(stats.montantMoyen)} F`} color="#e65100" icon="&#128176;" />
            <KpiCard label="Prix Moyen m³" value={`${fmt(stats.prixMoyenM3)} F`} color="#6a1b9a" icon="&#128178;" />
            <KpiCard label="Non Factures" value={fmt(stats.nbNonFactures)} color={stats.nbNonFactures > 0 ? '#c62828' : '#2e7d32'} icon="&#9888;" />
            <KpiCard label="Charges Totales" value={`${fmt(stats.totalCharges)} F`} color="#795548" icon="&#128181;" />
            <KpiCard label="Marge Nette" value={`${fmt(stats.margeNette)} F`} color={stats.margeNette >= 0 ? '#2e7d32' : '#c62828'} icon="&#128179;" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
            <div>
              {/* ==== GLOBAL VIEW ==== */}
              {!hasPartenaire && stats.parPartenaire && (
                <>
                  <div style={cardStyle}>
                    <h3 style={cardTitle}>Tableau de bord global par partenaire</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table" style={{ fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th>Partenaire</th>
                            <th>Abonnes</th>
                            <th>Factures</th>
                            <th>Volume (m<sup>3</sup>)</th>
                            <th>Montant TTC</th>
                            <th>Total Paye</th>
                            <th>Impayes</th>
                            <th>Taux Recouv.</th>
                            <th>Production</th>
                            <th>Rendement</th>
                            <th>Charges</th>
                            <th>Marge</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.parPartenaire.map((p: any, i: number) => (
                            <tr key={i}>
                              <td><strong>{p.partenaire || '-'}</strong></td>
                              <td>{fmt(p.nbAbonnes)}</td>
                              <td>{fmt(p.nbFactures)}</td>
                              <td>{fmt(p.volume)}</td>
                              <td>{fmt(p.montantTTC)}</td>
                              <td style={{ color: '#2e7d32', fontWeight: 600 }}>{fmt(p.totalPaye)}</td>
                              <td style={{ color: '#c62828', fontWeight: 600 }}>{fmt(p.resteARecouvrer)}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <div style={{ flex: 1, background: '#eee', borderRadius: 4, height: 8, minWidth: 40 }}>
                                    <div style={{ width: `${Math.min(p.tauxRecouvrement, 100)}%`, height: '100%', borderRadius: 4, background: kpiColor(p.tauxRecouvrement, 70, 40) }} />
                                  </div>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: kpiColor(p.tauxRecouvrement, 70, 40) }}>{p.tauxRecouvrement}%</span>
                                </div>
                              </td>
                              <td>{fmt(p.production)}</td>
                              <td style={{ color: p.rendement > 0 ? kpiColor(p.rendement, 80, 60) : '#999', fontWeight: 600 }}>
                                {p.rendement > 0 ? `${p.rendement}%` : '-'}
                              </td>
                              <td>{fmt(p.totalCharges)}</td>
                              <td style={{ color: p.margeNette >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{fmt(p.margeNette)}</td>
                            </tr>
                          ))}
                          {stats.parPartenaire.length > 1 && (() => {
                            const t = stats.parPartenaire.reduce((a: any, p: any) => ({
                              nbAbonnes: a.nbAbonnes + p.nbAbonnes, nbFactures: a.nbFactures + p.nbFactures,
                              volume: a.volume + p.volume, montantTTC: a.montantTTC + p.montantTTC,
                              totalPaye: a.totalPaye + p.totalPaye, resteARecouvrer: a.resteARecouvrer + p.resteARecouvrer,
                              production: a.production + p.production, totalCharges: a.totalCharges + p.totalCharges,
                              margeNette: a.margeNette + p.margeNette
                            }), { nbAbonnes: 0, nbFactures: 0, volume: 0, montantTTC: 0, totalPaye: 0, resteARecouvrer: 0, production: 0, totalCharges: 0, margeNette: 0 });
                            const taux = t.montantTTC > 0 ? Math.round((t.totalPaye / t.montantTTC) * 1000) / 10 : 0;
                            const rend = t.production > 0 ? Math.round((t.volume / t.production) * 1000) / 10 : 0;
                            return (
                              <tr style={{ background: '#f5f5f5', fontWeight: 700, borderTop: '2px solid #333' }}>
                                <td>TOTAL</td>
                                <td>{fmt(t.nbAbonnes)}</td><td>{fmt(t.nbFactures)}</td><td>{fmt(t.volume)}</td>
                                <td>{fmt(t.montantTTC)}</td>
                                <td style={{ color: '#2e7d32' }}>{fmt(t.totalPaye)}</td>
                                <td style={{ color: '#c62828' }}>{fmt(t.resteARecouvrer)}</td>
                                <td style={{ fontWeight: 700, color: kpiColor(taux, 70, 40) }}>{taux}%</td>
                                <td>{fmt(t.production)}</td>
                                <td style={{ color: kpiColor(rend, 80, 60) }}>{rend > 0 ? `${rend}%` : '-'}</td>
                                <td>{fmt(t.totalCharges)}</td>
                                <td style={{ color: t.margeNette >= 0 ? '#2e7d32' : '#c62828' }}>{fmt(t.margeNette)}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Charts globaux */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
                    <div style={cardStyle}>
                      <h3 style={cardTitle}>Taux de recouvrement par partenaire</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={stats.parPartenaire} margin={{ bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="partenaire" angle={-35} textAnchor="end" fontSize={10} interval={0} height={70} />
                          <YAxis unit="%" />
                          <Tooltip formatter={(v: any) => `${v}%`} />
                          <Bar dataKey="tauxRecouvrement" name="Taux (%)">
                            {stats.parPartenaire.map((_: any, i: number) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={cardStyle}>
                      <h3 style={cardTitle}>Parts des recouvrements</h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                            {pieData.map((_, i) => <Cell key={i} fill={COLORS_PIE[i]} />)}
                          </Pie>
                          <Tooltip formatter={(v: any) => fmt(Number(v)) + ' FCFA'} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}

              {/* ==== PARTNER VIEW ==== */}
              {hasPartenaire && stats.repartitionCategories && (
                <>
                  <div style={cardStyle}>
                    <h3 style={cardTitle}>Repartition des abonnes par categorie</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ minWidth: 160 }}></th>
                            {stats.repartitionCategories.map((c: any) => <th key={c.categorie} style={{ minWidth: 90 }}>{c.categorie || '-'}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td style={rowLabel}>Abonnes</td>{stats.repartitionCategories.map((c: any) => <td key={c.categorie}>{fmt(c.nbAbonnes)}</td>)}</tr>
                          <tr><td style={rowLabel}>Factures</td>{stats.repartitionCategories.map((c: any) => <td key={c.categorie}>{fmt(c.nbFactures)}</td>)}</tr>
                          <tr><td style={rowLabel}>Volume (m<sup>3</sup>)</td>{stats.repartitionCategories.map((c: any) => <td key={c.categorie}>{fmt(c.volume)}</td>)}</tr>
                          <tr><td style={rowLabel}>Montant (FCFA)</td>{stats.repartitionCategories.map((c: any) => <td key={c.categorie}>{fmt(c.montant)}</td>)}</tr>
                          <tr>
                            <td style={{ ...rowLabel, background: '#2e7d32', color: '#fff' }}>Total paye (Nb / Montant)</td>
                            {stats.repartitionCategories.map((c: any) => (
                              <td key={c.categorie} style={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}>
                                {fmt(c.nbPayes)} - {fmt(c.montantPaye)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td style={{ ...rowLabel, background: '#c62828', color: '#fff' }}>Total impaye (Nb / Montant)</td>
                            {stats.repartitionCategories.map((c: any) => (
                              <td key={c.categorie} style={{ background: '#ffebee', color: '#c62828', fontWeight: 600 }}>
                                {fmt(c.nbImpayes)} - {fmt(c.montantImpaye)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Top 5 secteurs impayés */}
                  {stats.topSecteursImpayes && stats.topSecteursImpayes.length > 0 && (
                    <div style={{ ...cardStyle, marginTop: 20 }}>
                      <h3 style={cardTitle}>Top 5 secteurs avec le plus d'impayes</h3>
                      <table className="data-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr><th>Secteur</th><th>Factures</th><th>Montant TTC</th><th>Paye</th><th>Impayes</th><th>Taux</th></tr>
                        </thead>
                        <tbody>
                          {stats.topSecteursImpayes.map((s: any, i: number) => {
                            const taux = s.montantTTC > 0 ? Math.round((s.totalPaye / s.montantTTC) * 1000) / 10 : 0;
                            return (
                              <tr key={i}>
                                <td><strong>{s.secteur || '-'}</strong></td>
                                <td>{fmt(s.nbFactures)}</td>
                                <td>{fmt(s.montantTTC)}</td>
                                <td style={{ color: '#2e7d32', fontWeight: 600 }}>{fmt(s.totalPaye)}</td>
                                <td style={{ color: '#c62828', fontWeight: 600 }}>{fmt(s.impayes)}</td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <div style={{ flex: 1, background: '#eee', borderRadius: 4, height: 8, minWidth: 40 }}>
                                      <div style={{ width: `${Math.min(taux, 100)}%`, height: '100%', borderRadius: 4, background: kpiColor(taux, 70, 40) }} />
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: kpiColor(taux, 70, 40) }}>{taux}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Charts partenaire */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
                    <div style={cardStyle}>
                      <h3 style={cardTitle}>Parts de consommation par categorie</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={stats.repartitionCategories.filter((c: any) => c.volume > 0)} dataKey="volume" nameKey="categorie" cx="50%" cy="50%" outerRadius={85}>
                            {stats.repartitionCategories.map((_: any, i: number) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v: any) => fmt(Number(v)) + ' m³'} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={cardStyle}>
                      <h3 style={cardTitle}>Parts des recouvrements</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
                            {pieData.map((_, i) => <Cell key={i} fill={COLORS_PIE[i]} />)}
                          </Pie>
                          <Tooltip formatter={(v: any) => fmt(Number(v)) + ' FCFA'} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Charges par type */}
                  {stats.chargesParType && stats.chargesParType.length > 0 && (
                    <div style={{ ...cardStyle, marginTop: 20 }}>
                      <h3 style={cardTitle}>Repartition des charges</h3>
                      <table className="data-table" style={{ fontSize: 13 }}>
                        <thead><tr><th>Type de charge</th><th>Nombre</th><th>Montant (FCFA)</th><th>%</th></tr></thead>
                        <tbody>
                          {stats.chargesParType.map((c: any, i: number) => (
                            <tr key={i}>
                              <td><strong>{c.type_charge || '-'}</strong></td>
                              <td>{c.nb}</td>
                              <td>{fmt(c.montant)}</td>
                              <td>{stats.totalCharges > 0 ? Math.round((c.montant / stats.totalCharges) * 100) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* === SIDE STATS === */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SideStat bg="#FF9800" label="Montant Total Attendu" value={`${fmt((stats.montantTTC || 0) + (stats.soldeAnterieur || 0))} FCFA`} />
              <SideStat bg="#e65100" label="Montant TTC Facture" value={`${fmt(stats.montantTTC)} FCFA`} />
              <SideStat bg="#78909C" label="Solde Anterieur" value={`${fmt(stats.soldeAnterieur)} FCFA`} />
              <SideStat bg="#f44336" label="Taxes / Surtaxes" value={`${fmt(stats.taxes)} / ${fmt(stats.surtaxes)}`} />
              <SideStat bg="#4CAF50" label="Total Recouvre" value={`${fmt(stats.totalPaye)} FCFA`} />
              <SideStat bg="#c62828" label="Montant a Recouvrer" value={`${fmt(stats.montantARecouvrer)} FCFA`} />
              <SideStat bg="#795548" label="Total Charges" value={`${fmt(stats.totalCharges)} FCFA`} />
              <SideStat bg={stats.margeNette >= 0 ? '#2e7d32' : '#c62828'} label="Marge Nette (Recouv. - Charges)" value={`${fmt(stats.margeNette)} FCFA`} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; color: string; icon: string }> = ({ label, value, color, icon }) => (
  <div style={{
    background: '#fff', borderRadius: 10, padding: '14px 16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: `4px solid ${color}`,
    display: 'flex', alignItems: 'center', gap: 10
  }}>
    <span style={{ fontSize: 22 }}>{icon}</span>
    <div>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  </div>
);

const SideStat: React.FC<{ bg: string; label: string; value: string }> = ({ bg, label, value }) => (
  <div style={{
    background: bg, color: '#fff', borderRadius: 10, padding: '14px 16px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
  }}>
    <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.9, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
  </div>
);

const selStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.3)',
  background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, cursor: 'pointer'
};

const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
};

const cardTitle: React.CSSProperties = { margin: '0 0 15px', color: '#333', fontSize: 16 };

const rowLabel: React.CSSProperties = {
  background: '#333', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap'
};

export default Statistiques;
